/**
 * The "explain everything" glossary.
 *
 * Audience: someone opening an astrology app for the first time with zero
 * background. Copy rules (docs/PRODUCT_COPY.md apply here too):
 *  - `short` is <= 90 characters, plain English, no jargon inside the jargon.
 *  - `long` is 2-4 sentences: what it means, where it comes from, why it
 *    matters to the reader.
 *  - Never claim astrology predicts events or is scientifically established.
 *    Describe what a term MEANS inside the system that uses it.
 *
 * IDs are stable and are also the deep-link anchors (`/glossary#nakshatra`).
 * Chart-fact ids from packages/astro-engine/src/chart/facts.ts resolve through
 * `findTerm` — `jyotish.lagna`, `jyotish.yoga.gajakesari`, `gochar.saturn`,
 * `western.mc` and friends all land on the right entry — so a tappable chart
 * fact can open its explanation without a second lookup table.
 */

export type GlossaryCategory = 'jyotish' | 'western' | 'shared' | 'product';

export interface GlossaryTerm {
  id: string;
  title: string;
  short: string;
  long: string;
  category: GlossaryCategory;
  aliases?: string[];
  /**
   * The Western/common-usage equivalent, when one exists ("Aries" for Mesha,
   * "the Sun" for Surya). Structured so the Term popover can render a quiet
   * "Western: Aries" line without parsing it back out of `long`.
   */
  western?: string;
}

export const CATEGORY_LABELS: Record<GlossaryCategory, string> = {
  shared: 'Start here — both systems',
  jyotish: 'Jyotish (Indian astrology)',
  western: 'Western astrology',
  product: 'Astrally',
};

/** Display order on the glossary screen. */
export const CATEGORY_ORDER: GlossaryCategory[] = ['shared', 'jyotish', 'western', 'product'];

// --- Shared -----------------------------------------------------------------

const SHARED: GlossaryTerm[] = [
  {
    id: 'precession',
    title: 'Precession of the equinoxes',
    short: 'Why your Western and Vedic signs differ — and why both are right.',
    long:
      "Earth's axis wobbles like a slowing spinning top, taking about 25,800 years for one full turn, and that drags the spring equinox slowly backwards through the constellations. Western astrology pins Aries to the equinox, so its signs stay locked to the seasons; Jyotish pins Aries to the fixed stars, so its signs stay locked to the constellations. The two lined up around the fifth century CE and have drifted apart by roughly one degree every 72 years since — about 24° today. That is why your Sun can be Leo in one system and Cancer in the other: two reference frames measuring the same sky, not an error in either.",
    category: 'shared',
    aliases: [
      'why my signs are different',
      'sidereal vs tropical',
      'vedic vs western',
      'two zodiacs',
      'different sun sign',
      '24 degrees',
    ],
  },
  {
    id: 'zodiac',
    title: 'Zodiac',
    short: 'The 12-sign band the Sun, Moon and planets appear to travel along.',
    long:
      "The zodiac is a belt of sky about eight degrees either side of the Sun's apparent yearly path, cut into twelve equal 30° signs. Both Western and Indian astrology use twelve signs with the same names and the same order — they simply start counting from different places. Constellations are real, unequal star patterns; signs are equal mathematical slices, which is why the two never line up perfectly.",
    category: 'shared',
    aliases: ['zodiac signs', 'the zodiac', 'ecliptic'],
  },
  {
    id: 'natal-chart',
    title: 'Natal chart',
    short: 'A map of the sky from your exact birth time and place.',
    long:
      'A natal or birth chart is a snapshot: where every planet was, seen from the exact spot on Earth you were born, at the minute you were born. Everything else in astrology is derived from it. Astrally calculates yours in both the Jyotish and Western frames from a single set of birth details.',
    category: 'shared',
    aliases: ['birth chart', 'natal', 'chart', 'horoscope'],
  },
  {
    id: 'birth-time',
    title: 'Why birth time matters',
    short: 'The minute sets your rising sign, your houses and your dasha start.',
    long:
      'Planets move slowly enough that most of your chart is fixed by your birth date alone, but the rising degree travels a full circle every 24 hours — so an hour of error can move your rising sign, your entire house layout and where your dasha timeline begins. That is why Astrally asks how confident you are, and why anything time-dependent is hidden rather than quietly guessed when you are not sure. If you narrow your time later, everything recalculates.',
    category: 'shared',
    aliases: ['timeConfidence', 'birth time', 'unknown birth time', 'rectification', 'meta'],
  },
  {
    id: 'retrograde',
    title: 'Retrograde',
    short: 'A planet appearing to move backwards, seen from a moving Earth.',
    long:
      'Planets do not actually reverse. Earth overtakes them on the inside track, and from our moving vantage point they appear to loop backwards against the stars for weeks or months. Every planet except the Sun and Moon does it. Both traditions read a retrograde planet as expressing itself inwardly or unusually — and Jyotish, unlike popular Western usage, often reads it as strengthened rather than broken.',
    category: 'shared',
    aliases: ['rx', 'retrogression', 'vakri', 'retro'],
  },
];

// --- Jyotish ----------------------------------------------------------------

const JYOTISH_CORE: GlossaryTerm[] = [
  {
    id: 'lagna',
    title: 'Lagna',
    short: "The sign climbing over the eastern horizon at your birth.",
    long:
      'Lagna is the zodiac sign that was rising in the east at the exact moment and place you were born. Because the sky turns a full circle every 24 hours it changes roughly every two hours, which is why an accurate birth time matters so much. In Jyotish the lagna becomes house 1 and every other house is counted from it, so it quietly reorganises your whole chart.',
    category: 'jyotish',
    aliases: ['lagnam', 'udaya lagna', 'vedic ascendant', 'vedic rising sign', 'janma lagna'],
  },
  {
    id: 'rashi',
    title: 'Rashi',
    short: 'A sign of the sidereal zodiac — the 30° slice a planet sits in.',
    long:
      'Rashi is simply the Sanskrit word for a zodiac sign: one of twelve 30-degree slices, from Mesha (Aries) to Meena (Pisces). Jyotish measures them against the fixed stars rather than the seasons, so your rashi is usually one sign earlier than the Western sign you already know. When someone asks for your "Vedic sign", they almost always mean your Moon rashi.',
    category: 'jyotish',
    aliases: ['rasi', 'vedic sign', 'moon rashi', 'chandra rashi', 'rashis'],
  },
  {
    id: 'bhava',
    title: 'Bhava (house)',
    short: 'A house — one of twelve areas of life your chart is divided into.',
    long:
      'Bhava means house: twelve compartments counted from your lagna covering body, money, siblings, home, creativity, work, partnership and so on. A graha sitting in a bhava is read as activity in that part of life. Houses need a birth time, which is why Astrally hides them entirely when your time is unknown instead of guessing.',
    category: 'jyotish',
    aliases: ['bhav', 'vedic house', 'jyotish house', 'bhavas'],
  },
  {
    id: 'graha',
    title: 'Graha',
    short: 'A "planet" in Jyotish — nine of them, including Rahu and Ketu.',
    long:
      'Graha literally means "the one that grasps". Jyotish uses nine: the Sun, Moon, Mars, Mercury, Jupiter, Venus and Saturn, plus Rahu and Ketu — the two points where the Moon\'s path crosses the Sun\'s. Rahu and Ketu are not physical bodies, and Jyotish does not traditionally use Uranus, Neptune or Pluto, which is one reason the two systems read the same birth differently.',
    category: 'jyotish',
    // 'rahu' and 'ketu' used to be aliases here; they now resolve to the
    // dedicated graha.rahu / graha.ketu entries below.
    aliases: ['navagraha', 'grahas', 'vedic planets'],
  },
  {
    id: 'nakshatra',
    title: 'Nakshatra',
    short: 'One of 27 lunar mansions — the finer grid Jyotish reads the Moon on.',
    long:
      "The zodiac is cut a second way in Jyotish: 27 nakshatras of 13°20' each, based on where the Moon lands night by night. Your birth nakshatra is the one your Moon occupied, and it is the single most used data point in Indian astrology — naming, matching and timing all start there. Each nakshatra carries a ruling graha, and that is what sets your dasha sequence.",
    category: 'jyotish',
    aliases: ['nakshatras', 'lunar mansion', 'birth star', 'janma nakshatra', 'star'],
  },
  {
    id: 'dasha',
    title: 'Dasha',
    short: 'A planetary period — Jyotish saying which graha is currently on duty.',
    long:
      "A dasha is a stretch of time ruled by one graha. Timing is Jyotish's signature move: rather than only describing your chart, it divides your life into nested periods and reads each through the graha that governs it. Astrally uses the Vimshottari system, calculated from your birth nakshatra.",
    category: 'jyotish',
    aliases: ['dashas', 'dasa', 'planetary period', 'dasha period'],
  },
  {
    id: 'mahadasha',
    title: 'Mahadasha',
    short: 'The big outer period — one graha colours 6 to 20 years of your life.',
    long:
      'The mahadasha is the largest layer of the dasha system, and each one runs between six and twenty years depending on which graha rules it. It is the backdrop that the smaller periods play out against. Astrally shows which mahadasha you are in now and the exact dates it runs between.',
    category: 'jyotish',
    aliases: ['maha dasha', 'major period', 'mahadasa', 'mahadashaSpan', 'current'],
  },
  {
    id: 'antardasha',
    title: 'Antardasha',
    short: 'A sub-period inside a mahadasha — the layer that moves in months.',
    long:
      'Every mahadasha is divided into antardashas, sub-periods ruled by each of the nine grahas in turn. This is where Jyotish gets specific: the mahadasha sets the theme, the antardasha sets the chapter. Astrally shows both, and the level below (pratyantardasha) when your birth time is exact.',
    category: 'jyotish',
    aliases: ['antar dasha', 'bhukti', 'sub-period', 'pratyantardasha'],
  },
  {
    id: 'vimshottari',
    title: 'Vimshottari dasha',
    short: 'The 120-year timing cycle, started from your birth nakshatra.',
    long:
      'Vimshottari means "one hundred and twenty" — the length of the full cycle, split between the nine grahas in fixed amounts (Venus 20 years, Saturn 19, the Sun 6, and so on). Where you enter that cycle is decided by your Moon\'s nakshatra at birth. It is the most widely used timing system in Indian astrology, which is why Astrally makes it the default.',
    category: 'jyotish',
    aliases: ['vimshottari', 'vimsottari', 'vimshottari dasa'],
  },
  {
    id: 'gochara',
    title: 'Gochara (transits)',
    short: 'Where the planets are right now, read against your birth chart.',
    long:
      'Gochara is the moving sky: the actual positions of the grahas today, compared to the fixed pattern you were born with. Your natal chart never changes, so transits are how astrology talks about the present at all. Astrally counts gochara from your Moon, which is the Jyotish convention, and from your natal points in the Western view.',
    category: 'jyotish',
    aliases: ['gochar', 'transit', 'transits', 'gocharam'],
  },
  {
    id: 'ayanamsa',
    title: 'Ayanamsa',
    short: 'The gap between the two zodiacs — about 24° today, and still growing.',
    long:
      'Ayanamsa is the number you subtract from a tropical (Western) position to get a sidereal (Jyotish) one. It exists because the two systems chose different starting points and the equinox keeps drifting, widening the gap by roughly one degree every 72 years. Today it sits near 24°, which is exactly why your Western and Vedic signs are usually one apart.',
    category: 'jyotish',
    aliases: ['ayanamsha', 'ayanansa', 'ayanamsa offset'],
  },
  {
    id: 'sidereal',
    title: 'Sidereal zodiac',
    short: 'Signs measured against the fixed stars — the Jyotish frame.',
    long:
      'A sidereal zodiac ties the signs to the constellations, so Aries begins where a chosen fixed star says it does. Jyotish works in this frame. It is not more or less correct than the tropical zodiac — it answers a different question, which is why Astrally calculates both instead of picking a winner.',
    category: 'jyotish',
    aliases: ['nirayana', 'sidereal', 'star-based zodiac'],
  },
  {
    id: 'lahiri',
    title: 'Lahiri ayanamsa',
    short: "India's government-standard ayanamsa, and Astrally's default.",
    long:
      'Lahiri, also called Chitrapaksha, is the ayanamsa adopted by the Indian Calendar Reform Committee and used by almost every Indian panchanga — the usual default right across India. Astrally calculates with it unless you choose otherwise. Schools differ by only a few arcminutes, which changes a placement only when it already sits near the very edge of a sign.',
    category: 'jyotish',
    aliases: ['chitrapaksha', 'chitra paksha', 'lahiri'],
  },
  {
    id: 'raman',
    title: 'Raman ayanamsa',
    short: "B. V. Raman's ayanamsa — about 21 arcminutes behind Lahiri.",
    long:
      "Raman is the sidereal reference used by B. V. Raman's school, with a following in Karnataka and among readers trained in that lineage. It runs roughly 21 arcminutes behind Lahiri, so nearly every placement is identical and only positions at a sign boundary move. Switch to it in settings if the astrologer your family uses does.",
    category: 'jyotish',
    aliases: ['bv raman', 'b v raman', 'raman'],
  },
  {
    id: 'kp',
    title: 'KP (Krishnamurti Paddhati)',
    short: 'A twentieth-century South Indian system with its own ayanamsa.',
    long:
      'KP, short for Krishnamurti Paddhati, was developed by K. S. Krishnamurti and is very widely used in Tamil Nadu and the wider South. It is known for subdividing the nakshatras into finer "sub" lords, and for its own ayanamsa about five arcminutes behind Lahiri. Astrally offers the KP ayanamsa; the full KP reading method is a separate technique.',
    category: 'jyotish',
    aliases: ['krishnamurti', 'krishnamurti paddhati', 'kp ayanamsa', 'kp system'],
  },
  {
    id: 'north-indian-chart',
    title: 'North Indian chart style',
    short: 'The diamond chart: the houses stay fixed and the signs move.',
    long:
      'In the North Indian style the chart is a square with diamonds inside it, and each box is a house — house 1 always sits at the top centre. The sign number is written inside the box, so it is the signs that shift from chart to chart. If you learned charts anywhere in the Hindi belt, this is the shape you picture.',
    category: 'jyotish',
    aliases: ['north indian', 'diamond chart', 'north chart'],
  },
  {
    id: 'south-indian-chart',
    title: 'South Indian chart style',
    short: 'The fixed grid: the signs never move and the lagna is marked.',
    long:
      'The South Indian style is a 4×4 grid where every box is permanently one sign — Mesha (Aries) sits second along the top row and the rest run clockwise — so the signs never move between charts. The lagna is marked, usually with a diagonal stroke, and houses are counted clockwise from there. It is the standard across Tamil Nadu, Kerala, Andhra and Karnataka.',
    category: 'jyotish',
    aliases: ['south indian', 'rasi chakra', 'south chart'],
  },
  {
    id: 'east-indian-chart',
    title: 'East Indian chart style',
    short: 'The Bengali and Odia style — a square crossed by diagonals.',
    long:
      'The East Indian style, used in Bengal, Odisha and Assam, draws the chart as a square crossed by diagonals, producing eight triangles and four corner boxes. Like the South Indian style the signs hold fixed positions and the lagna is marked. It is exactly the same chart as the other two styles — only the drawing convention differs.',
    category: 'jyotish',
    aliases: ['east indian', 'bengali chart', 'odia chart'],
  },
  {
    id: 'varga',
    title: 'Varga (divisional chart)',
    short: 'The same birth moment re-sliced at a finer resolution.',
    long:
      'Vargas are charts built by dividing each 30° sign into smaller parts and treating those parts as signs in their own right. Each one is traditionally read for a specific subject: D9 for partnership, D10 for career, and so on. Because they magnify tiny slices of the zodiac, a varga is only ever as reliable as your birth time.',
    category: 'jyotish',
    aliases: ['vargas', 'divisional chart', 'divisional charts', 'amsa', 'amsha'],
  },
  {
    id: 'd1',
    title: 'D1 (rashi chart)',
    short: 'Your main birth chart — planets in the signs they actually occupy.',
    long:
      'D1 is the rashi chart: the birth chart itself, with every graha in the sign it truly sits in. Every divisional chart is derived from it. When Astrally says "your chart" without qualifying it, this is the one it means.',
    category: 'jyotish',
    aliases: ['d-1', 'rashi chart', 'rasi chart', 'birth chart (vedic)'],
  },
  {
    id: 'd9',
    title: 'D9 (navamsha)',
    short: 'The ninth-part chart — read for marriage and underlying strength.',
    long:
      'D9 divides each sign into nine parts and rebuilds the chart from them, so small differences in birth time change it a great deal. It is traditionally read for marriage, dharma and inner strength — a graha that looks weak in D1 but strong here is treated very differently. It is the second chart most Jyotish readers open, right after D1.',
    category: 'jyotish',
    aliases: ['navamsa', 'navamsha', 'd-9', 'navamsa chart'],
  },
  {
    id: 'd10',
    title: 'D10 (dashamsha)',
    short: 'The tenth-part chart — read for career and public life.',
    long:
      'D10 divides each sign into ten parts and is the chart traditionally consulted for work, status and how you show up in public. Like every varga it is a re-slicing of the same birth moment, not extra information. It needs an accurate birth time to mean anything.',
    category: 'jyotish',
    aliases: ['dasamsa', 'dashamsa', 'd-10', 'dasamsa chart'],
  },
  {
    id: 'yoga',
    title: 'Yoga',
    short: 'A named combination in a chart — a pattern with a recognised meaning.',
    long:
      'A yoga is a specific arrangement of grahas that the classical texts gave a name to, such as Gajakesari (Jupiter in a kendra from the Moon). Astrally states yogas as structural facts — what is actually in your chart — never as predictions about your life. Some need an exact birth time, and those stay hidden rather than guessed when yours is uncertain.',
    category: 'jyotish',
    aliases: ['yogas', 'combination', 'raj yog'],
  },
  {
    id: 'dosha',
    title: 'Dosha',
    short: 'A named combination read as friction rather than support.',
    long:
      'Dosha is the counterpart to yoga: a pattern traditionally read as a difficulty, or an area that needs care. The best-known doshas carry real social weight in some families and are treated as minor, or cancelled outright, by other schools. Astrally reports what is structurally present and where traditions disagree — it does not tell you what to do about it.',
    category: 'jyotish',
    aliases: ['doshas', 'dosh'],
  },
  {
    id: 'manglik',
    title: 'Mangal dosha (manglik)',
    short: 'Mars in house 1, 2, 4, 7, 8 or 12 — the most argued-about dosha.',
    long:
      'Mangal dosha, popularly "manglik", is the classical rule that Mars sitting in the 1st, 2nd, 4th, 7th, 8th or 12th house is a friction point for marriage. Traditions genuinely disagree: some count from the Moon instead of the lagna, some exclude certain signs, and many treat it as cancelled when both people have it. Astrally reports whether the structural rule is met, from the lagna and from the Moon, and leaves the weighting to you.',
    category: 'jyotish',
    aliases: [
      'mangal dosha',
      'mangalik',
      'manglik dosha',
      'kuja dosha',
      'chevvai dosham',
      'mangal-dosha',
      'mangal-dosha-moon',
      'mars dosha',
    ],
  },
  {
    id: 'sade-sati',
    title: 'Sade sati',
    short: "Saturn's roughly 7½-year passage over and around your Moon sign.",
    long:
      'Sade sati literally means "seven and a half": the stretch when transiting Saturn crosses the sign before your Moon sign, your Moon sign itself, and the one after — about two and a half years each. It has a fearsome popular reputation; classical readings treat it as demanding, slow and consolidating rather than as a sentence. Everyone gets one roughly every thirty years.',
    category: 'jyotish',
    aliases: ['sadesati', 'shani sade sati', 'sade sathi', 'saturn 7.5 years'],
  },
  {
    id: 'combust',
    title: 'Combust',
    short: 'A planet too close to the Sun to be seen — "burnt" in Jyotish terms.',
    long:
      'A graha within a few degrees of the Sun is called combust (astangata) — literally lost in the glare, invisible from Earth. Classical texts read a combust graha as struggling to express itself on its own terms because the Sun dominates it. Mercury and Venus are combust often, since they never stray far from the Sun to begin with.',
    category: 'jyotish',
    aliases: ['astangata', 'combustion', 'burnt', 'combusted'],
  },
  {
    id: 'exalted',
    title: 'Exalted',
    short: 'A planet in the sign where it is traditionally strongest.',
    long:
      'Each graha has one sign where it is said to be exalted — the Sun in Aries, the Moon in Taurus, Saturn in Libra, and so on. It is a dignity rating from the classical texts, meaning the planet expresses itself cleanly there. Both Jyotish and Western astrology use exaltation, with the same sign assignments.',
    category: 'jyotish',
    aliases: ['exaltation', 'uccha', 'exalt'],
  },
  {
    id: 'debilitated',
    title: 'Debilitated',
    short: 'A planet in the sign opposite its exaltation — its weakest dignity.',
    long:
      'Debilitation (neecha) is exaltation\'s opposite: the sign directly across the zodiac, where a graha is read as least at home. It does not mean something is wrong with you — the same texts describe conditions that cancel it, and a debilitated graha is usually described as working differently rather than badly. Astrally reports the placement and leaves the interpretation as a conversation.',
    category: 'jyotish',
    aliases: ['debilitation', 'neecha', 'fall', 'debility'],
  },
  {
    id: 'atmakaraka',
    title: 'Atmakaraka',
    short: 'The planet at the highest degree in your chart — the "soul indicator".',
    long:
      'In Jaimini astrology the graha sitting at the highest degree within its sign, whichever sign that happens to be, becomes your atmakaraka. It is read as the significator of what your life keeps circling back to. It is one of the few chart factors that usually survives an uncertain birth time, since degrees change slowly for everything except the Moon.',
    category: 'jyotish',
    aliases: ['atma karaka', 'ak', 'soul planet'],
  },
  {
    id: 'kendra',
    title: 'Kendra',
    short: 'Houses 1, 4, 7 and 10 — the load-bearing angles of a chart.',
    long:
      'Kendras are the four angular houses counted from the lagna, and Jyotish treats them as the pillars a chart stands on. A graha in a kendra is considered to have a clear channel to act. Several yogas, including Gajakesari, are defined by something landing in a kendra.',
    category: 'jyotish',
    aliases: ['kendras', 'angular houses', 'quadrant houses'],
  },
  {
    id: 'trikona',
    title: 'Trikona',
    short: 'Houses 1, 5 and 9 — the trine houses, read as the lucky ones.',
    long:
      'Trikonas are the 1st, 5th and 9th houses from the lagna, traditionally the most benefic positions in a chart and associated with dharma, merit and support. When a kendra lord and a trikona lord link up, classical texts call the result a raja yoga. They are the Jyotish counterpart of the Western trine.',
    category: 'jyotish',
    aliases: ['trikonas', 'trine houses'],
  },
  {
    id: 'gajakesari',
    title: 'Gajakesari Yoga',
    short: 'Jupiter in a kendra (1, 4, 7 or 10) from the Moon.',
    long:
      'Gajakesari forms when Jupiter sits in an angle from the Moon — the "elephant and lion" combination of the classical texts, associated with steadiness and good counsel. Astrally reports it as a structural fact about where your Jupiter and Moon actually are. It does not need your birth time, because it is measured from the Moon rather than the lagna.',
    category: 'jyotish',
    aliases: ['gaja kesari', 'gajakesari yoga'],
  },
  {
    id: 'budhaditya',
    title: 'Budhaditya Yoga',
    short: 'The Sun and Mercury together in the same sign.',
    long:
      'Budhaditya is simply Mercury conjunct the Sun, which is common — Mercury never travels far from the Sun. Classically it is associated with intelligence and articulacy, and it overlaps with Mercury being combust, so readers weigh the exact distance. Astrally states the placement and lets the reading stay a discussion.',
    category: 'jyotish',
    aliases: ['budha aditya', 'budhaditya yoga'],
  },
  {
    id: 'chandra-mangala',
    title: 'Chandra-Mangala Yoga',
    short: 'The Moon and Mars together in the same sign.',
    long:
      'Chandra-Mangala forms when the Moon and Mars occupy one sign, combining feeling with drive. Classical texts link it to enterprise and to earning through effort. It needs no birth time, since neither point is measured from the lagna.',
    category: 'jyotish',
    aliases: ['chandra mangala', 'moon mars yoga'],
  },
  {
    id: 'kemadruma',
    title: 'Kemadruma Yoga',
    short: 'No graha beside the Moon — the classical "unsupported Moon" rule.',
    long:
      'Kemadruma is counted when no graha other than the Sun and the nodes sits with the Moon or in the sign either side of it. Traditionally it is read as an isolated Moon, and the same texts list many conditions that cancel it. Astrally states the base rule only, and says so, rather than implying a verdict.',
    category: 'jyotish',
    aliases: ['kemdrum', 'kemadruma yoga'],
  },
  {
    id: 'raja-kendra-trikona',
    title: 'Raja Yoga',
    short: 'A kendra lord and a trikona lord joining forces in your chart.',
    long:
      'Raja yogas are the "royal" combinations: a connection between the ruler of an angular house and the ruler of a trine house, which classical texts read as capability meeting opportunity. Astrally checks the simplified conjunction form of the rule. It needs an exact birth time, because both lordships are counted from the lagna.',
    category: 'jyotish',
    aliases: ['raja yoga', 'raj yoga', 'rajyoga'],
  },
  {
    id: 'kaal-sarpa',
    title: 'Kaal Sarpa',
    short: 'All seven grahas sitting on one side of the Rahu–Ketu axis.',
    long:
      'Kaal Sarpa is a whole-chart pattern: every classical graha hemmed between Rahu and Ketu, with none on the other side. It is a modern favourite in popular astrology and is not found in the oldest texts, which is worth knowing before anyone charges you to fix it. Astrally reports whether the geometry is present, nothing more.',
    category: 'jyotish',
    aliases: ['kaal sarp', 'kal sarpa', 'kalsarpa dosha'],
  },
  {
    id: 'kundali',
    title: 'Kundali',
    short: 'Your birth chart in the Indian tradition — the whole document.',
    long:
      'Kundali (or kundli, or janam patri) is the North Indian word for a natal chart, usually meaning the full package: the rashi chart, the divisional charts, the dasha timeline and the panchanga details of your birth. In the South you will hear jatakam. It is the same underlying calculation Astrally runs for you.',
    category: 'jyotish',
    aliases: ['kundli', 'janam kundali', 'jatakam', 'janam patri', 'kundali chart'],
  },
  {
    id: 'kundali-milan',
    title: 'Kundali milan',
    short: 'Chart matching — comparing two kundalis before a marriage.',
    long:
      'Kundali milan is the practice of comparing two birth charts for compatibility, usually via the 36-point ashtakoota score plus checks for doshas like mangal. In India it is a social ritual as much as an astrological one. Astrally shows the calculation and its limits: it is Moon-based arithmetic, not evidence about a person.',
    category: 'jyotish',
    aliases: ['kundli milan', 'horoscope matching', 'patrika milan', 'match making'],
  },
  {
    id: 'ashtakoota',
    title: 'Ashtakoota',
    short: 'The 36-point Moon-based compatibility score used in Indian matchmaking.',
    long:
      'Ashtakoota, also called guna milan, compares two people across eight kootas worth 36 points in total, all calculated from the Moon\'s sign and nakshatra. It is the number families quote when they say a match "scored 28 out of 36". Astrally computes it because people ask for it — but it uses the Moon alone, ignores everything you actually know about a person, and is never proof that a relationship will or will not work.',
    category: 'jyotish',
    aliases: ['ashta koota', 'guna milan', 'gun milan', 'ashtakoot', '36 points', 'ashtkoot'],
  },
  {
    id: 'guna',
    title: 'Guna',
    short: 'A point in guna milan — 36 available, and the number families quote.',
    long:
      'Guna means a quality or point in the ashtakoota score. When someone says a match "got 28 gunas", they mean 28 of the 36 points available across the eight kootas. The familiar thresholds — 18 to pass, 24 for a good match — are conventions, not rules laid down in any single authoritative text.',
    category: 'jyotish',
    aliases: ['gunas', 'guna points', 'gun'],
  },
  {
    id: 'varna',
    title: 'Varna koota',
    short: '1 point for the work-temperament class of the two Moon signs.',
    long:
      'Varna is the smallest koota, worth a single point, grouping the twelve rashis into four classes traditionally associated with types of work and temperament. The classical rule is directional: it checks whether one person\'s class sits at or above the other\'s. It carries obvious historical baggage, and Astrally reports the calculation without endorsing what it was once used to justify.',
    category: 'jyotish',
    aliases: ['varna', 'varan koot'],
  },
  {
    id: 'vashya',
    title: 'Vashya koota',
    short: '2 points for how the two Moon signs are said to influence each other.',
    long:
      'Vashya sorts the rashis into groups — four-footed, human, water-dwelling, wild and insect — and scores how naturally one is thought to hold sway over the other. It is worth 2 of the 36 points. Read it as an old shorthand about who leads and who follows, not as a description of anybody\'s character.',
    category: 'jyotish',
    aliases: ['vashya', 'vasya', 'vashya kuta'],
  },
  {
    id: 'tara',
    title: 'Tara koota',
    short: '3 points for the nakshatra count between two people, both ways.',
    long:
      'Tara counts the distance from one person\'s birth nakshatra to the other\'s, in both directions, and scores whether each count lands on a group traditionally read as supportive. It is worth 3 points. The same counting logic is used in Jyotish for choosing auspicious days, not just for matching people.',
    category: 'jyotish',
    aliases: ['tara', 'tara bala', 'tara kuta'],
  },
  {
    id: 'yoni',
    title: 'Yoni koota',
    short: '4 points from the animal symbol each birth nakshatra carries.',
    long:
      'Every nakshatra is paired with an animal — horse, elephant, serpent, deer and so on — and yoni scores whether the two animals are the same, neutral, or a traditional enemy pair. It is worth 4 points and is usually glossed as instinctive and physical compatibility. Astrally uses the same, enemy or otherwise rule and says so, rather than implying more precision than it has.',
    category: 'jyotish',
    aliases: ['yoni', 'yoni kuta'],
  },
  {
    id: 'graha-maitri',
    title: 'Graha maitri koota',
    short: '5 points for whether the two Moon-sign rulers are friends or enemies.',
    long:
      'Graha maitri takes the ruling graha of each person\'s Moon sign and looks up their classical friendship — friend, neutral or enemy, in both directions. It is worth 5 points and is usually described as mental rapport. The friendship table is fixed and traditional: Saturn and the Sun are enemies in it regardless of what the two people are like.',
    category: 'jyotish',
    aliases: ['grahaMaitri', 'graha maitri', 'grahamaitri', 'maitri'],
  },
  {
    id: 'gana',
    title: 'Gana koota',
    short: '6 points for temperament type: deva, manushya or rakshasa.',
    long:
      'Gana sorts the 27 nakshatras into three temperaments — deva (gentle), manushya (mixed, human) and rakshasa (forceful) — and awards all 6 points when both people share one. The names sound dramatic in English; they sit closer to "easy-going", "ordinary" and "intense" than to good and evil. A deva–rakshasa pairing scores lowest.',
    category: 'jyotish',
    aliases: ['gana', 'gan koot'],
  },
  {
    id: 'bhakoot',
    title: 'Bhakoot koota',
    short: '7 points from the distance between the two Moon signs.',
    long:
      'Bhakoot scores the relative positions of two Moon signs and is worth 7 points, the second largest koota. Certain distances — 2–12, 5–9 and 6–8 — score zero, and that is what people mean by bhakoot dosha. Because so much weight rides on one comparison, a single sign either way can swing a whole score.',
    category: 'jyotish',
    aliases: ['bhakut', 'rashi koota', 'bhakoot kuta'],
  },
  {
    id: 'bhakoot-dosha',
    title: 'Bhakoot dosha',
    short: 'The Moon-sign gaps that score zero on the 7-point bhakoot koota.',
    long:
      'When two Moon signs fall 2–12, 5–9 or 6–8 apart, bhakoot scores zero out of 7 and the result is called bhakoot dosha. It is one of the more commonly waived rules in practice, particularly when other kootas score well. Like every koota it is a formula about two Moon positions, not a measure of how two people treat each other.',
    category: 'jyotish',
    aliases: ['bhakut dosha', 'bhakoot dosh'],
  },
  {
    id: 'nadi',
    title: 'Nadi koota',
    short: '8 points — the heaviest koota, scored from nakshatra groups.',
    long:
      'Nadi divides the 27 nakshatras into three cycling groups (adi, madhya and antya) and awards all 8 points when two people differ, zero when they match. It is the single largest koota, so it dominates the total on its own. Its traditional justification is constitution and health, and it remains the most disputed rule in the whole system.',
    category: 'jyotish',
    aliases: ['nadi kuta', 'naadi'],
  },
  {
    id: 'nadi-dosha',
    title: 'Nadi dosha',
    short: 'Both people sharing a nadi group — 8 of the 36 points, gone.',
    long:
      'Nadi dosha is what happens when two birth nakshatras fall in the same nadi group: the largest koota scores zero, and a total can collapse from one comparison. Many practitioners treat it as cancelled in several situations, and the schools do not agree on which. Astrally never presents a score, or a dosha, as a verdict on a relationship.',
    category: 'jyotish',
    aliases: ['nadi dosh', 'naadi dosha'],
  },
  {
    id: 'panchanga',
    title: 'Panchanga',
    short: 'The Indian almanac: five daily measures used to judge a day.',
    long:
      'Panchanga means "five limbs" — tithi, vaara, nakshatra, yoga and karana — the five measures that describe any given day in the Indian calendar. It is what a traditional almanac lists and what timing decisions are made from. The panchanga of the day you were born is part of your kundali.',
    category: 'jyotish',
    aliases: ['panchang', 'panchangam', 'almanac', 'pancanga'],
  },
  {
    id: 'tithi',
    title: 'Tithi',
    short: 'A lunar day — one of 30 steps from new Moon to full and back.',
    long:
      'A tithi is the time the Moon takes to gain 12° on the Sun, so it is a lunar day rather than a solar one and can stretch or shrink against the clock. Fifteen tithis run from new Moon to full and fifteen return. Indian festivals are dated by tithi, which is why they land on a different Gregorian date each year.',
    category: 'jyotish',
    aliases: ['tithis', 'lunar day'],
  },
  {
    id: 'vaara',
    title: 'Vaara',
    short: 'The weekday, each one ruled by a graha — Sunday is the Sun\'s.',
    long:
      'Vaara is the weekday limb of the panchanga, and every day belongs to a graha: Sunday the Sun, Monday the Moon, Tuesday Mars, Wednesday Mercury, Thursday Jupiter, Friday Venus, Saturday Saturn. English kept the identical scheme through Norse and Latin names, which is why the two lists line up exactly. Traditional practice matches the task to the day\'s ruler.',
    category: 'jyotish',
    aliases: ['vara', 'vaar', 'weekday'],
  },
  {
    id: 'rahu-kaal',
    title: 'Rahu kaal',
    short: 'A daily 90-minute window traditionally avoided for starting things.',
    long:
      'Rahu kaal is a stretch of about an hour and a half each day, found by splitting daylight into eight parts and assigning one of them to Rahu — which part depends on the weekday and on your local sunrise. Many households in India will not begin travel, purchases or ceremonies during it. It is a convention about beginnings, not a claim that something bad happens.',
    category: 'jyotish',
    aliases: ['rahu kalam', 'rahukaal', 'rahu kaalam', 'rahu kal'],
  },
  {
    id: 'muhurtha',
    title: 'Muhurtha',
    short: 'Choosing an auspicious moment to begin something important.',
    long:
      'Muhurtha is the branch of Jyotish concerned with electing a moment — for a wedding, a launch, a journey — using the panchanga and the chart of that moment itself. It is the practical inverse of natal astrology: instead of reading a birth you already had, you choose a birth for an event. It is also where most day-to-day Indian astrology actually gets used.',
    category: 'jyotish',
    aliases: ['muhurat', 'muhurtam', 'electional astrology', 'muhurath'],
  },
];

// --- Nakshatras -------------------------------------------------------------

interface NakshatraRow {
  id: string;
  title: string;
  lord: string;
  short: string;
  body: string;
}

/** Lords follow NAKSHATRA_LORDS in @astrally/shared (Ketu → Mercury, ×3). */
const NAKSHATRA_ROWS: NakshatraRow[] = [
  {
    id: 'ashwini', title: 'Ashwini', lord: 'Ketu',
    short: "The horse's head — speed, fresh starts and instinctive healing.",
    body: "Ashwini opens the cycle at the start of Aries, symbolised by a horse's head and linked to the twin physicians of the Rig Veda.",
  },
  {
    id: 'bharani', title: 'Bharani', lord: 'Venus',
    short: 'The womb — holding, restraint and the weight of what you carry.',
    body: "Bharani's symbol is the yoni, the vessel that holds and then delivers; its themes are creation, limits and endurance.",
  },
  {
    id: 'krittika', title: 'Krittika', lord: 'Sun',
    short: 'The blade and the flame — cutting through to what is true.',
    body: 'Krittika is symbolised by a razor and by fire, and is associated with Agni; the tradition reads it as sharp discrimination.',
  },
  {
    id: 'rohini', title: 'Rohini', lord: 'Moon',
    short: 'The red one — growth, beauty and things that ripen.',
    body: "Rohini is the Moon's favourite nakshatra in myth, symbolised by an ox cart and associated with fertility and material growth.",
  },
  {
    id: 'mrigashira', title: 'Mrigashira', lord: 'Mars',
    short: "The deer's head — curiosity, searching, never quite settled.",
    body: "Mrigashira's symbol is a deer's head turning to catch a scent; the theme is seeking rather than arriving.",
  },
  {
    id: 'ardra', title: 'Ardra', lord: 'Rahu',
    short: 'The teardrop — storm, release and the clarity that follows.',
    body: 'Ardra is linked to Rudra, the storm, and symbolised by a single teardrop; it covers upheaval and the clearing air afterwards.',
  },
  {
    id: 'punarvasu', title: 'Punarvasu', lord: 'Jupiter',
    short: 'The return of the light — recovery, home and second chances.',
    body: 'Punarvasu means "good again"; its symbol is a quiver of arrows and its theme is renewal after loss.',
  },
  {
    id: 'pushya', title: 'Pushya', lord: 'Saturn',
    short: 'Nourishment — the most protective nakshatra in the tradition.',
    body: "Pushya is symbolised by a cow's udder and a flower, and is considered one of the most auspicious nakshatras for beginning anything.",
  },
  {
    id: 'ashlesha', title: 'Ashlesha', lord: 'Mercury',
    short: 'The coiled serpent — insight, influence and hidden depth.',
    body: 'Ashlesha is symbolised by a coiled snake and associated with the nagas; the tradition reads it as penetrating, hypnotic intelligence.',
  },
  {
    id: 'magha', title: 'Magha', lord: 'Ketu',
    short: 'The throne — ancestry, lineage and inherited authority.',
    body: 'Magha means "the mighty" and is symbolised by a royal throne; its deities are the pitris, the ancestors.',
  },
  {
    id: 'purva-phalguni', title: 'Purva Phalguni', lord: 'Venus',
    short: 'The hammock — pleasure, rest and creative play.',
    body: 'Purva Phalguni is symbolised by the front legs of a bed and associated with Bhaga, the god of delight and good fortune.',
  },
  {
    id: 'uttara-phalguni', title: 'Uttara Phalguni', lord: 'Sun',
    short: 'The other half of the bed — partnership and steady support.',
    body: 'Uttara Phalguni completes the pair, symbolised by the back legs of the bed and linked to Aryaman, keeper of agreements.',
  },
  {
    id: 'hasta', title: 'Hasta', lord: 'Moon',
    short: 'The hand — skill, craft and making things with precision.',
    body: "Hasta's symbol is an open palm, and its themes are dexterity and putting something concrete into the world.",
  },
  {
    id: 'chitra', title: 'Chitra', lord: 'Mars',
    short: 'The jewel — design, brilliance and things made beautiful.',
    body: 'Chitra means "bright" or "many-coloured", symbolised by a shining gem and associated with Vishvakarma, the celestial architect.',
  },
  {
    id: 'swati', title: 'Swati', lord: 'Rahu',
    short: 'The shoot bending in wind — independence and flexibility.',
    body: 'Swati is symbolised by a young shoot moving in the breeze and its deity is Vayu, the wind; the theme is self-directed movement.',
  },
  {
    id: 'vishakha', title: 'Vishakha', lord: 'Jupiter',
    short: 'The archway — ambition, focus and arriving at a goal.',
    body: "Vishakha's symbols are a triumphal arch and a potter's wheel, and it is linked to Indra and Agni; it is read as determined pursuit.",
  },
  {
    id: 'anuradha', title: 'Anuradha', lord: 'Saturn',
    short: 'The lotus — friendship, devotion and loyalty that lasts.',
    body: "Anuradha's symbol is a lotus and its deity Mitra, god of friendship and contracts; its theme is bonds you choose rather than inherit.",
  },
  {
    id: 'jyeshtha', title: 'Jyeshtha', lord: 'Mercury',
    short: 'The eldest — seniority, protection and the cost of responsibility.',
    body: 'Jyeshtha means "the eldest", symbolised by a circular amulet and associated with Indra and with the weight of being in charge.',
  },
  {
    id: 'mula', title: 'Mula', lord: 'Ketu',
    short: 'The root — digging to the bottom of a thing, whatever it costs.',
    body: 'Mula means "root", symbolised by a bunch of tied roots and associated with Nirriti; its theme is getting to the foundation.',
  },
  {
    id: 'purva-ashadha', title: 'Purva Ashadha', lord: 'Venus',
    short: 'The unconquered — conviction, and refusing to be moved.',
    body: 'Purva Ashadha means "the earlier invincible one", symbolised by a winnowing fan and linked to the waters; the theme is unshakeable belief.',
  },
  {
    id: 'uttara-ashadha', title: 'Uttara Ashadha', lord: 'Sun',
    short: 'The later victory — endurance that wins slowly and keeps it.',
    body: "Uttara Ashadha completes the pair, symbolised by an elephant's tusk and associated with the Vishvadevas; it is read as durable success.",
  },
  {
    id: 'shravana', title: 'Shravana', lord: 'Moon',
    short: 'The ear — listening, learning and carrying knowledge accurately.',
    body: 'Shravana means "hearing", symbolised by an ear and by Vishnu\'s three strides; it is the nakshatra of knowledge received from others.',
  },
  {
    id: 'dhanishta', title: 'Dhanishta', lord: 'Mars',
    short: 'The drum — rhythm, wealth and the pulse of a group.',
    body: "Dhanishta's symbol is a drum and its deities are the eight Vasus; its themes are prosperity, music and timing.",
  },
  {
    id: 'shatabhisha', title: 'Shatabhisha', lord: 'Rahu',
    short: 'The hundred healers — medicine, mystery and things kept private.',
    body: 'Shatabhisha means "a hundred physicians", symbolised by an empty circle and linked to Varuna; it covers healing and secrecy.',
  },
  {
    id: 'purva-bhadrapada', title: 'Purva Bhadrapada', lord: 'Jupiter',
    short: 'The first of the twins — intensity, idealism and burning off excess.',
    body: 'Purva Bhadrapada is symbolised by the front of a funeral cot and associated with Aja Ekapada; its theme is change through extremes.',
  },
  {
    id: 'uttara-bhadrapada', title: 'Uttara Bhadrapada', lord: 'Saturn',
    short: 'The depths — stillness, wisdom and hard-won calm.',
    body: 'Uttara Bhadrapada completes the pair, symbolised by the serpent of the deep waters; it is read as profound, settled understanding.',
  },
  {
    id: 'revati', title: 'Revati', lord: 'Mercury',
    short: 'The last one — safe passage, endings and looking after others.',
    body: 'Revati closes the cycle at the end of Pisces, symbolised by a fish and by Pushan, the guide who protects travellers.',
  },
];

const NAKSHATRAS: GlossaryTerm[] = NAKSHATRA_ROWS.map((n) => ({
  id: n.id,
  title: `${n.title} (nakshatra)`,
  short: n.short,
  long: `${n.body} Its ruling graha is ${n.lord}, so a Moon born here starts the Vimshottari dasha cycle in ${n.lord}'s period. That is what makes your birth nakshatra the anchor of Jyotish timing rather than just a label.`,
  category: 'jyotish',
  aliases: [n.title, `${n.title} nakshatra`],
}));

// --- Rashis -----------------------------------------------------------------

interface RashiRow {
  /** Bare Sanskrit name; the id becomes `rashi.<name>`. */
  id: string;
  title: string;
  western: string;
  symbol: string;
  /** chara / sthira / dvisvabhava, with the English gloss beside it. */
  quality: string;
  /** agni / prithvi / vayu / jala, with the English gloss beside it. */
  element: string;
  ruler: string;
  short: string;
  body: string;
}

const RASHI_ROWS: RashiRow[] = [
  {
    id: 'mesha', title: 'Mesha', western: 'Aries', symbol: 'a ram',
    quality: 'chara (movable)', element: 'agni (fire)', ruler: 'Mangal (Mars)',
    short: 'The ram — first to move, direct, and happiest starting something new.',
    body: 'Mesha opens the sidereal zodiac, and the tradition reads it as initiative in its purest form: act first, polish later.',
  },
  {
    id: 'vrishabha', title: 'Vrishabha', western: 'Taurus', symbol: 'a bull',
    quality: 'sthira (fixed)', element: 'prithvi (earth)', ruler: 'Shukra (Venus)',
    short: 'The bull — steady, sensory, and impossible to rush once settled.',
    body: 'Vrishabha is the zodiac settling in: comfort, worth and things built to last all live here.',
  },
  {
    id: 'mithuna', title: 'Mithuna', western: 'Gemini', symbol: 'a couple in conversation',
    quality: 'dvisvabhava (dual)', element: 'vayu (air)', ruler: 'Budh (Mercury)',
    short: 'The couple — curious, quick with words, always mid-conversation.',
    body: 'Mithuna is the rashi of exchange: questions, messages and ideas passed from hand to hand.',
  },
  {
    id: 'karka', title: 'Karka', western: 'Cancer', symbol: 'a crab',
    quality: 'chara (movable)', element: 'jala (water)', ruler: 'Chandra (the Moon)',
    short: 'The crab — protective, tidal, and led by feeling before logic.',
    body: 'Karka is the rashi of shelter: it holds, feeds and remembers, and it guards what it loves.',
  },
  {
    id: 'simha', title: 'Simha', western: 'Leo', symbol: 'a lion',
    quality: 'sthira (fixed)', element: 'agni (fire)', ruler: 'Surya (the Sun)',
    short: 'The lion — warm, proud, and most alive with an audience.',
    body: 'Simha is the heart on display: generosity, dignity and the wish to be seen doing something well.',
  },
  {
    id: 'kanya', title: 'Kanya', western: 'Virgo', symbol: 'a maiden holding grain',
    quality: 'dvisvabhava (dual)', element: 'prithvi (earth)', ruler: 'Budh (Mercury)',
    short: 'The maiden with grain — precise, useful, quietly improving things.',
    body: 'Kanya takes what is rough and refines it; service and craft are its native register.',
  },
  {
    id: 'tula', title: 'Tula', western: 'Libra', symbol: 'a pair of scales',
    quality: 'chara (movable)', element: 'vayu (air)', ruler: 'Shukra (Venus)',
    short: 'The scales — weighing, balancing, allergic to an unfair room.',
    body: 'Tula is the rashi of the other person: agreement, proportion and keeping the peace without losing the point.',
  },
  {
    id: 'vrishchika', title: 'Vrishchika', western: 'Scorpio', symbol: 'a scorpion',
    quality: 'sthira (fixed)', element: 'jala (water)', ruler: 'Mangal (Mars)',
    short: 'The scorpion — private, intense, and all-in once it commits.',
    body: 'Vrishchika deals in depth: what is hidden, what is shared and what refuses to stay on the surface.',
  },
  {
    id: 'dhanu', title: 'Dhanu', western: 'Sagittarius', symbol: 'a drawn bow',
    quality: 'dvisvabhava (dual)', element: 'agni (fire)', ruler: 'Guru (Jupiter)',
    short: 'The bow — aimed at something further out: belief, travel, truth.',
    body: 'Dhanu is the rashi of the long shot: it wants the bigger picture and says what it thinks on the way there.',
  },
  {
    id: 'makara', title: 'Makara', western: 'Capricorn', symbol: 'the makara, a sea-crocodile of myth',
    quality: 'chara (movable)', element: 'prithvi (earth)', ruler: 'Shani (Saturn)',
    short: 'The sea-crocodile — patient, structural, playing the long game.',
    body: 'Makara climbs slowly and does not slide back; it builds things designed to outlast the effort of building them.',
  },
  {
    id: 'kumbha', title: 'Kumbha', western: 'Aquarius', symbol: 'a water pot',
    quality: 'sthira (fixed)', element: 'vayu (air)', ruler: 'Shani (Saturn)',
    short: 'The water pot — principled, a little detached, loyal to the idea.',
    body: 'Kumbha carries something for everyone: systems, causes and the group weighed above the individual.',
  },
  {
    id: 'meena', title: 'Meena', western: 'Pisces', symbol: 'two fish swimming opposite ways',
    quality: 'dvisvabhava (dual)', element: 'jala (water)', ruler: 'Guru (Jupiter)',
    short: 'The two fish — porous, imaginative, hard to pin to one shore.',
    body: 'Meena closes the cycle by softening its edges: empathy, imagination and the sense that everything connects.',
  },
];

const RASHIS: GlossaryTerm[] = RASHI_ROWS.map((r) => ({
  id: `rashi.${r.id}`,
  title: r.title,
  short: r.short,
  long: `${r.body} Its symbol is ${r.symbol}, and in Jyotish terms it is a ${r.quality} ${r.element} rashi ruled by ${r.ruler}. Western equivalent: ${r.western}.`,
  category: 'jyotish',
  aliases: [r.title, `${r.title} rashi`, r.western],
  western: r.western,
}));

// --- Grahas -----------------------------------------------------------------

interface GrahaRow {
  /** Bare Sanskrit name; the id becomes `graha.<name>`. */
  id: string;
  title: string;
  western: string;
  short: string;
  long: string;
  aliases: string[];
}

const GRAHA_ROWS: GrahaRow[] = [
  {
    id: 'surya', title: 'Surya', western: 'the Sun',
    short: 'The Sun — the self at full wattage: vitality, authority, father.',
    long:
      'Surya is the Sun, and in Jyotish it is the karaka — the natural significator — of the self, the father, authority and sheer vitality. Its nature is hot and kingly: it clarifies whatever it touches, though a graha sitting too close is called combust, lost in its glare. It rules Simha (Leo), and the part of a chart it occupies is read as where you cannot help being visible.',
    aliases: ['Surya', 'Sun', 'ravi'],
  },
  {
    id: 'chandra', title: 'Chandra', western: 'the Moon',
    short: 'The Moon — the mind, moods, memory, and everything that nourishes.',
    long:
      "Chandra is the Moon, the karaka of the mind, the mother, memory and emotional weather. Jyotish leans on it harder than any other graha: your Moon's rashi and nakshatra set your dasha timeline and drive most everyday readings. Its nature is cool and receptive, counted strongest when bright and waxing, and it rules Karka (Cancer).",
    aliases: ['Chandra', 'Moon', 'soma'],
  },
  {
    id: 'mangal', title: 'Mangal', western: 'Mars',
    short: 'Mars — drive, courage, siblings, and the will to push through.',
    long:
      'Mangal is Mars: the karaka of energy, courage, younger siblings and land. Its nature is hot and blunt — it cuts, competes and defends, which reads as bravery in one chart and friction in another. It rules Mesha (Aries) and Vrishchika (Scorpio), and it is the graha behind the much-argued mangal dosha rule.',
    aliases: ['Mangal', 'Mars', 'mangala', 'kuja'],
  },
  {
    id: 'budh', title: 'Budh', western: 'Mercury',
    short: 'Mercury — speech, wit, trade, and everything you learn quickly.',
    long:
      'Budh is Mercury, the karaka of speech, intellect, commerce and skill with the hands. It is the adaptable one: the classical texts call it neutral, taking on the nature of whichever graha it sits with. It rules Mithuna (Gemini) and Kanya (Virgo), and it is exalted in Kanya — a sign it also owns.',
    aliases: ['Budh', 'Mercury', 'budha'],
  },
  {
    id: 'guru', title: 'Guru', western: 'Jupiter',
    short: 'Jupiter — wisdom, teachers, children, and doors that open.',
    long:
      'Guru is Jupiter, and the name means teacher: it is the karaka of wisdom, children, wealth and faith. Jyotish treats it as the great benefic, the graha whose gaze is said to protect whatever it falls on. It rules Dhanu (Sagittarius) and Meena (Pisces).',
    aliases: ['Guru', 'Jupiter', 'brihaspati'],
  },
  {
    id: 'shukra', title: 'Shukra', western: 'Venus',
    short: 'Venus — love, beauty, comfort, and the art of enjoying life.',
    long:
      'Shukra is Venus, the karaka of love, marriage, beauty and comfort — the finer things, and the relationships they are enjoyed with. It is the other great benefic, and in the Vimshottari system it holds the longest dasha of all, a full twenty years. It rules Vrishabha (Taurus) and Tula (Libra).',
    aliases: ['Shukra', 'Venus'],
  },
  {
    id: 'shani', title: 'Shani', western: 'Saturn',
    short: 'Saturn — time, discipline, labour, and rewards that arrive late.',
    long:
      'Shani is Saturn, the slowest of the classical grahas and the karaka of time, work, grief and endurance. Its reputation is stern, but the tradition is more precise: Shani delays rather than denies, and what it gives after the wait tends to stay. It rules Makara (Capricorn) and Kumbha (Aquarius), and its slow transit over your Moon sign is what Jyotish calls sade sati.',
    aliases: ['Shani', 'Saturn'],
  },
  {
    id: 'rahu', title: 'Rahu', western: 'the North Node',
    short: 'The north node — appetite, obsession, and hunger for the new.',
    long:
      "Rahu is not a physical body: it is the north node, the point where the Moon's path crosses the Sun's on the way up, and Jyotish promotes it to a full graha. It is read as appetite — for the foreign, the modern and the not-yet-had — and it amplifies whatever part of the chart it occupies. It rules no sign of its own, expressing itself through the graha that rules the sign it sits in. Western astrology calls the same point the North Node.",
    aliases: ['Rahu', 'north node', "dragon's head"],
  },
  {
    id: 'ketu', title: 'Ketu', western: 'the South Node',
    short: 'The south node — release, detachment, and what feels finished.',
    long:
      "Ketu is the south node, Rahu's mirror point where the Moon's path crosses the Sun's on the way down — the other shadow graha with no physical body. Where Rahu is hunger, Ketu is the already-eaten: it marks what you can do without trying and no longer find exciting. The tradition associates it with detachment, insight and moksha, the loosening of attachments. Western astrology calls the same point the South Node.",
    aliases: ['Ketu', 'south node', "dragon's tail"],
  },
];

const GRAHAS: GlossaryTerm[] = GRAHA_ROWS.map((g) => ({
  id: `graha.${g.id}`,
  title: g.title,
  short: g.short,
  long: g.long,
  category: 'jyotish',
  aliases: g.aliases,
  western: g.western,
}));

// --- Bhavas -----------------------------------------------------------------

interface BhavaRow {
  n: number;
  name: string;
  /** One or two words for the title's bracket. */
  gist: string;
  /** The life areas in one breath, <= 90 chars. */
  short: string;
  /** What living topics land here, with one concrete everyday example. */
  long: string;
}

const BHAVA_ROWS: BhavaRow[] = [
  {
    n: 1, name: 'Tanu', gist: 'self',
    short: 'You: body, temperament, and the first impression you leave.',
    long:
      'Tanu means body: this is you yourself — health, temperament, and the impression you make before you have said anything. The lagna sits here, which is why the whole chart is counted from it. It is the bhava being read when someone glances at a kundli and starts with "so, about you".',
  },
  {
    n: 2, name: 'Dhan', gist: 'money & speech',
    short: 'Money, possessions, speech, and the family you come from.',
    long:
      'Dhan covers what you gather and hold: earned money, possessions, food, and — distinctively in Jyotish — your speech and the family you were raised in. It is the savings account and the dinner-table voice in one bhava. A question like "will I manage to save this year?" is traditionally read from here.',
  },
  {
    n: 3, name: 'Sahaj', gist: 'siblings & courage',
    short: 'Siblings, courage, short trips, and everyday messages.',
    long:
      'Sahaj is the near-at-hand: younger siblings, neighbours, short trips, messages, and the courage to put yourself forward. It covers skills learned by doing rather than studying. Texting a sibling to plan a weekend trip is this bhava working at full strength.',
  },
  {
    n: 4, name: 'Sukh', gist: 'home & heart',
    short: 'Home, mother, land, vehicles, and inner peace.',
    long:
      'Sukh literally means happiness: home, mother, land, vehicles, and the private base you retreat to. It is the most inward bhava, the part of life nobody sees from outside. The feeling of finally being back in your own bed after travelling is a Sukh bhava feeling.',
  },
  {
    n: 5, name: 'Putra', gist: 'children & creativity',
    short: 'Children, romance, creativity, and intelligence.',
    long:
      'Putra covers children, romance, creativity, intelligence and — in the tradition — merit carried in from before. It is what you make for the joy of making it. A hobby that eats a whole Sunday without feeling like work belongs here.',
  },
  {
    n: 6, name: 'Ari', gist: 'work & obstacles',
    short: 'Daily work, health, debts, and the fights you can win.',
    long:
      'Ari means enemy: daily work, health, debts, disputes and competition all land in this bhava. Jyotish treats a strong Ari bhava as being good in a fight rather than free of them. Clearing a backlog, a loan EMI, or a niggling health check-up are all sixth-bhava business.',
  },
  {
    n: 7, name: 'Yuvati', gist: 'partnership',
    short: 'Marriage, partnership, and the person across the table.',
    long:
      'Yuvati sits directly opposite Tanu and is the house of the other person: marriage, business partners, and anyone you formally face. It is the bhava every kundali milan conversation circles. Signing a rental agreement with a partner is as seventh-bhava as a wedding is.',
  },
  {
    n: 8, name: 'Randhra', gist: 'depths & change',
    short: 'Shared money, intimacy, secrets, and sudden change.',
    long:
      "Randhra covers what is shared rather than owned — inheritance, a spouse's finances, intimacy — along with research, secrets, longevity and change that arrives unscheduled. It is the bhava of what happens below the surface. A family property settlement is classic Randhra territory.",
  },
  {
    n: 9, name: 'Dharma', gist: 'belief & fortune',
    short: 'Belief, teachers, long journeys, and plain good fortune.',
    long:
      'Dharma is the long view: belief, philosophy, higher study, long journeys, teachers, and the father in many readings. Jyotish counts it the most fortunate bhava of all. Choosing a master\'s course abroad — the belief, the travel and the teacher in one decision — is a ninth-bhava move.',
  },
  {
    n: 10, name: 'Karma', gist: 'career',
    short: 'Career, action, and what you are publicly known for.',
    long:
      'Karma means action: career, reputation, and what strangers could tell about you from a distance. It is the most public point of the chart. A promotion, a job change, or your name on a piece of work — the tenth bhava is where it shows.',
  },
  {
    n: 11, name: 'Labha', gist: 'gains & friends',
    short: 'Gains, income, friends, and the hopes you are working towards.',
    long:
      'Labha means gains: income, profits, friends, networks and the hopes you are working towards. It is where individual effort turns into something collective. The group chat that eventually lands you a job lead is the eleventh bhava doing exactly what it does.',
  },
  {
    n: 12, name: 'Vyaya', gist: 'rest & release',
    short: 'Spending, sleep, retreat, foreign shores, and release.',
    long:
      'Vyaya means expenditure: spending, sleep, retreat, foreign shores, hospitals, and everything that happens offstage. Loss and release are the same movement seen from different sides, and both live here. Money quietly spent on rest — a holiday that fixes you — is the twelfth bhava at its kindest.',
  },
];

const BHAVA_ORDINALS = [
  '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th',
];

const BHAVAS: GlossaryTerm[] = BHAVA_ROWS.map((b) => ({
  id: `bhava.${b.n}`,
  title: `${BHAVA_ORDINALS[b.n - 1]} bhava — ${b.name} (${b.gist})`,
  short: b.short,
  long: b.long,
  category: 'jyotish',
  aliases: [`${b.name} bhava`, `bhava ${b.n}`, b.name],
  western: `${BHAVA_ORDINALS[b.n - 1]} house`,
}));

// --- Western ----------------------------------------------------------------

const WESTERN_CORE: GlossaryTerm[] = [
  {
    id: 'tropical-zodiac',
    title: 'Tropical zodiac',
    short: 'Signs measured from the spring equinox — the Western frame.',
    long:
      'The tropical zodiac starts Aries at the March equinox and divides the year evenly from there, so it tracks the seasons rather than the constellations. It is what every Western horoscope, app and magazine column uses. Because the equinox drifts against the stars, tropical positions now sit about 24° ahead of sidereal ones.',
    category: 'western',
    aliases: ['tropical', 'sayana', 'seasonal zodiac'],
  },
  {
    id: 'sun-sign',
    title: 'Sun sign',
    short: 'The sign the Sun was in when you were born — your "star sign".',
    long:
      'Your Sun sign is the one you already know, the answer to "what\'s your sign?". It needs only your birth date, which is why it is the single piece of astrology that fits in a headline. In a full chart it is one factor among dozens — and in Jyotish it is usually the previous sign, because of the different zodiac.',
    category: 'western',
    aliases: ['star sign', 'sun sign', 'zodiac sign', 'my sign'],
  },
  {
    id: 'moon-sign',
    title: 'Moon sign',
    short: 'Where the Moon was at birth — read as your private, inner register.',
    long:
      'The Moon changes sign roughly every two and a half days, so a Moon sign needs your date and at least a rough time of day. Western astrology reads it as emotional habit and what you need in order to feel settled. Jyotish leans on it far harder: your Moon\'s sign and nakshatra drive dashas, matching and most everyday readings.',
    category: 'western',
    aliases: ['lunar sign', 'janma rashi'],
  },
  {
    id: 'rising-sign',
    title: 'Rising sign (ascendant)',
    short: 'The sign on the eastern horizon at your birth — the AC.',
    long:
      'Your rising sign, ascendant or AC is the degree of the zodiac coming up over the horizon at your exact birth moment and place. It changes about every two hours, which makes it the part of a chart that most demands an accurate birth time. Western astrology reads it as your approach and first impression; Jyotish calls the very same point the lagna and builds its entire house structure from it.',
    category: 'western',
    aliases: ['ascendant', 'ac', 'asc', 'rising', 'rising sign'],
  },
  {
    id: 'midheaven',
    title: 'Midheaven (MC)',
    short: 'The highest point of the chart — read for career and reputation.',
    long:
      'The Midheaven, or MC (medium coeli), is where the ecliptic crosses the meridian directly overhead at your birth. It anchors the 10th house in most Western house systems and is read for vocation, public standing and what you are visibly known for. Like the ascendant it moves quickly, so it needs an accurate birth time.',
    category: 'western',
    aliases: ['mc', 'medium coeli', 'midheaven'],
  },
  {
    id: 'elements',
    title: 'Elements',
    short: 'Fire, earth, air and water — four temperaments across the signs.',
    long:
      'Every sign belongs to one of four elements: fire (Aries, Leo, Sagittarius), earth (Taurus, Virgo, Capricorn), air (Gemini, Libra, Aquarius) and water (Cancer, Scorpio, Pisces). They are the broadest grouping in the zodiac and the quickest way to read a chart\'s overall balance. Jyotish uses the same fourfold split under the names agni, prithvi, vayu and jala.',
    category: 'western',
    aliases: ['element', 'fire signs', 'earth signs', 'air signs', 'water signs', 'triplicity'],
  },
  {
    id: 'modalities',
    title: 'Modalities',
    short: 'Cardinal, fixed and mutable — how a sign prefers to move.',
    long:
      'The three modalities describe mode rather than flavour: cardinal signs start things (Aries, Cancer, Libra, Capricorn), fixed signs hold them (Taurus, Leo, Scorpio, Aquarius), and mutable signs adapt and hand them on (Gemini, Virgo, Sagittarius, Pisces). Each element appears once in each modality, which is how twelve signs stay distinct. Jyotish uses the same three groups under the names chara, sthira and dvisvabhava.',
    category: 'western',
    aliases: ['modality', 'quadruplicity', 'cardinal', 'fixed', 'mutable', 'quality'],
  },
  {
    id: 'aspect',
    title: 'Aspect',
    short: 'The angle between two planets — how they are said to talk.',
    long:
      'An aspect is a specific angular distance between two points in a chart, such as 90° or 120°, each with its own traditional meaning. Western astrology reads aspects as the conversation happening inside your chart. Jyotish uses a completely different scheme — whole-sign drishti — so the two systems will not list the same aspects for the same birth.',
    category: 'western',
    aliases: ['aspects', 'drishti', 'angle'],
  },
  {
    id: 'conjunction',
    title: 'Conjunction',
    short: '0° apart — two planets fused into one hard-to-separate voice.',
    long:
      'A conjunction places two planets in the same spot, so they act as a single blended influence rather than two separate ones. Whether that reads as concentration or overload depends entirely on which planets are involved. It is the one major aspect that both Western astrology and Jyotish treat much the same way.',
    category: 'western',
    aliases: ['conjunct', 'conjunctions'],
  },
  {
    id: 'opposition',
    title: 'Opposition',
    short: '180° apart — a standoff between two ends of the chart.',
    long:
      'An opposition puts two planets directly across from each other, each sitting in the other\'s blind spot. It is read as a tension that only resolves by holding both sides rather than choosing one. Oppositions always link signs of the same modality, which is why neither side gives way easily.',
    category: 'western',
    aliases: ['opposite', 'oppositions'],
  },
  {
    id: 'trine',
    title: 'Trine',
    short: '120° apart — easy flow between planets of the same element.',
    long:
      'A trine connects planets in the same element, which is why it is read as the most comfortable aspect: things move without friction. The standard caution is that easy is not the same as motivating, so trines can simply go unused. It is traditionally the most benefic of the major aspects.',
    category: 'western',
    aliases: ['trines', 'triangle aspect'],
  },
  {
    id: 'square',
    title: 'Square',
    short: '90° apart — friction between two planets that forces a move.',
    long:
      'A square links planets in the same modality but clashing elements, so both want to act and neither wants to yield. Western astrology reads it as the aspect that produces effort and, over time, competence. It is the usual source of a "this chart has tension" reading.',
    category: 'western',
    aliases: ['squares'],
  },
  {
    id: 'sextile',
    title: 'Sextile',
    short: '60° apart — a cooperative link that only works if you use it.',
    long:
      'A sextile joins planets in compatible elements, usually fire with air or earth with water. It is read as opportunity rather than event: available, but not automatic. It is milder than a trine and is normally given a tighter orb.',
    category: 'western',
    aliases: ['sextiles'],
  },
  {
    id: 'quincunx',
    title: 'Quincunx',
    short: '150° apart — two planets with nothing in common, awkwardly linked.',
    long:
      'The quincunx, also called the inconjunct, joins signs that share no element, modality or polarity, so the two planets have no natural way to negotiate. It is read as constant small adjustment rather than open conflict. It is a minor aspect and is only counted with a tight orb.',
    category: 'western',
    aliases: ['inconjunct', 'quincunxes'],
  },
  {
    id: 'semisextile',
    title: 'Semisextile',
    short: '30° apart — neighbouring signs that barely understand each other.',
    long:
      'A semisextile connects adjacent signs, which have nothing structural in common despite sitting side by side. It is a minor aspect, read as a low-level nudge or a slight discomfort. Most astrologers allow it an orb of a degree or two at most.',
    category: 'western',
    aliases: ['semi-sextile', 'semi sextile'],
  },
  {
    id: 'semisquare',
    title: 'Semisquare',
    short: '45° apart — half a square, and read as low-grade irritation.',
    long:
      'The semisquare is a minor hard aspect: the friction a square describes, at a quarter of the circle rather than a half. It usually shows up as a persistent niggle rather than a crisis. It is one of the aspects Kepler argued for, and not every tradition uses it.',
    category: 'western',
    aliases: ['semi-square', 'semi square', 'octile'],
  },
  {
    id: 'sesquiquadrate',
    title: 'Sesquiquadrate',
    short: '135° apart — a square and a half, another minor friction angle.',
    long:
      'The sesquiquadrate, or sesquisquare, is 90° plus 45°, and belongs to the same family of hard minor aspects as the semisquare. It is read as pressure that surfaces late, once a situation is already underway. Like the semisquare it is optional in many traditions and always given a tight orb.',
    category: 'western',
    aliases: ['sesquisquare', 'sesqui-square', 'trioctile'],
  },
  {
    id: 'orb',
    title: 'Orb',
    short: 'How far from exact an aspect can be and still count.',
    long:
      'Aspects are almost never perfectly exact, so astrologers allow an orb — a tolerance measured in degrees. A trine at 118° is still a trine, with an orb of 2°. Tighter orbs mean fewer but stronger aspects, and Astrally shows you the orb so you can judge for yourself how exact a connection really is.',
    category: 'western',
    aliases: ['orbs', 'orb of influence'],
  },
  {
    id: 'synastry',
    title: 'Synastry',
    short: 'Two birth charts compared point by point — Western compatibility.',
    long:
      "Synastry overlays one person's chart on another's and reads the aspects between them: your Venus to their Mars, their Saturn to your Moon. It is the Western counterpart of kundali milan, though it produces a description rather than a score out of 36. Astrally draws on signals from both traditions and treats neither as proof that a person is right, or safe, for you.",
    category: 'western',
    aliases: ['chart comparison', 'relationship astrology'],
  },
  {
    id: 'composite-chart',
    title: 'Composite chart',
    short: 'One chart built from the midpoints of two — "the relationship".',
    long:
      "A composite chart averages two people's planetary positions into a single new chart, treated as a portrait of the relationship rather than of either person. It is a twentieth-century technique and is not universally used. Where synastry describes how two people interact, a composite describes what they become together.",
    category: 'western',
    aliases: ['composite', 'midpoint chart'],
  },
  {
    id: 'solar-return',
    title: 'Solar return',
    short: 'The chart for the moment the Sun returns to its birth degree.',
    long:
      'Once a year, usually within a day of your birthday, the Sun comes back to the exact degree it held when you were born, and a solar return chart is cast for that instant. It is read as a picture of the year ahead. Where you physically are at that moment changes the chart\'s houses, which is why some people travel for it.',
    category: 'western',
    aliases: ['solar return chart', 'birthday chart', 'varshphal'],
  },
  {
    id: 'progressions',
    title: 'Progressions',
    short: 'A symbolic clock: one day after birth stands for one year of life.',
    long:
      'Secondary progressions advance your birth chart by one day for every year you have lived, so your chart at thirty is the sky as it looked thirty days after you were born. It is a symbolic technique rather than a physical one, used for slow inner development rather than events. The progressed Moon changes sign about every two and a half years and is the piece most watched.',
    category: 'western',
    aliases: ['progression', 'secondary progressions', 'progressed chart'],
  },
  {
    id: 'saturn-return',
    title: 'Saturn return',
    short: 'Saturn back where it started — at roughly 29, 58 and 87.',
    long:
      'Saturn takes about 29.5 years to orbit the Sun, so it returns to its birth position at around 29, 58 and 87. Western astrology reads each return as a structural audit: what you built without deciding to, and what you now have to choose deliberately. It is probably the best-known transit in popular astrology, and Jyotish covers similar ground with sade sati.',
    category: 'western',
    aliases: ['saturn returns', 'first saturn return'],
  },
  {
    id: 'mercury-retrograde',
    title: 'Mercury retrograde',
    short: 'Mercury appearing to move backwards — three or four times a year.',
    long:
      'Mercury retrograde is an optical effect: as Earth overtakes Mercury, the planet appears to reverse against the stars for roughly three weeks. Popular culture attaches it to lost emails and travel chaos, which is far more weight than the tradition itself gives it. Both systems read retrograde motion as a change in how a planet expresses, not as a scheduled disaster.',
    category: 'western',
    aliases: ['mercury rx', 'merc retro', 'mercury retro'],
  },
  {
    id: 'part-of-fortune',
    title: 'Part of Fortune',
    short: 'A point calculated from your Sun, Moon and ascendant — not a planet.',
    long:
      'The Part of Fortune, or Lot of Fortune, is an Arabic part: a point derived arithmetically from the positions of your Sun, Moon and ascendant rather than observed in the sky. It comes from Hellenistic astrology and is traditionally read for wellbeing and where things come easily. Because it uses the ascendant, it needs an accurate birth time.',
    category: 'western',
    aliases: ['lot of fortune', 'pars fortunae', 'fortuna'],
  },
  {
    id: 'house-systems',
    title: 'House systems',
    short: 'Rival methods for slicing a chart into twelve houses.',
    long:
      'Astrologers agree on where the planets are and disagree on where the house lines fall. Different systems divide space, time or the ecliptic in different ways, so the same planet can land in the 9th house in one system and the 10th in another. Astrally names the system it is using rather than letting the difference hide.',
    category: 'western',
    aliases: ['house system', 'house division'],
  },
  {
    id: 'whole-sign',
    title: 'Whole sign houses',
    short: 'One sign, one house — the oldest and simplest house system.',
    long:
      'In whole sign houses the sign containing your ascendant becomes the entire 1st house, the next sign the 2nd, and so on around. It is the system of Hellenistic astrology and is effectively what Jyotish uses too, which makes it the easiest bridge between the two traditions. It also degrades gracefully when your birth time is slightly off.',
    category: 'western',
    aliases: ['whole sign', 'whole sign house system'],
  },
  {
    id: 'placidus',
    title: 'Placidus',
    short: 'The most common modern house system — houses of unequal size.',
    long:
      'Placidus divides the time a degree takes to rise from the horizon to the midheaven, which produces houses of very uneven size and breaks down entirely at extreme latitudes. It is the default in most Western software, so it is what you will have seen if you have looked up your chart before. It depends on an accurate birth time more than whole sign does.',
    category: 'western',
    aliases: ['placidus houses'],
  },
];

// --- Western houses ---------------------------------------------------------

const HOUSE_ROWS: Array<{ n: number; short: string; long: string }> = [
  {
    n: 1,
    short: 'Self, body, and the impression you make before you speak.',
    long: 'The 1st house begins at your ascendant and works as the front door of the chart: physical body, temperament, and how you land in a room. Planets here tend to be obvious to other people before you say anything. Jyotish calls it the lagna bhava and counts every other house from it.',
  },
  {
    n: 2,
    short: 'Money you earn, what you own, and what you value keeping.',
    long: 'The 2nd house covers what you gather and hold: earned income, possessions, and what you were taught to treat as valuable. It is resources in the broadest sense. Jyotish adds speech and the family you came from to the same house.',
  },
  {
    n: 3,
    short: 'Siblings, short trips and everyday communication.',
    long: 'The 3rd house is the near-at-hand: siblings, neighbours, short journeys, messages, and skills picked up by repetition. It is the house of ordinary daily contact. Jyotish also reads courage and self-effort here.',
  },
  {
    n: 4,
    short: 'Home, roots, family and your private base.',
    long: 'The 4th house sits at the very bottom of the chart and covers home, land, roots, and the parent who made you feel safe. It is the most private part of a chart, the part nobody sees from outside. Jyotish reads it for property, vehicles and inner contentment.',
  },
  {
    n: 5,
    short: 'Creativity, romance, play and children.',
    long: 'The 5th house is what you make for the pleasure of making it: creative work, romance, play, speculation and children. It is output that is not employment. Jyotish also reads it for intelligence and for merit carried from before.',
  },
  {
    n: 6,
    short: 'Daily work, routines, health and obligations.',
    long: 'The 6th house covers routine, service, health, debts and the maintenance that keeps a life running. It also holds conflict and competition. Jyotish reads it for illness, enemies and disputes, and treats a strong 6th as being good at a fight rather than free of them.',
  },
  {
    n: 7,
    short: 'Partnership — marriage, close allies and open opponents.',
    long: 'The 7th house sits directly opposite the 1st and is the house of the other person: marriage, business partners and declared opponents. Anyone you formally face is found here. Jyotish weighs it heavily in any marriage analysis.',
  },
  {
    n: 8,
    short: 'Shared resources, intimacy, inheritance and transformation.',
    long: 'The 8th house covers what you share rather than own: joint finances, inheritance, other people\'s resources, and intimacy. It is also the house of endings and of change you did not schedule. Jyotish reads it for longevity and for sudden turns.',
  },
  {
    n: 9,
    short: 'Belief, higher study, long journeys and teachers.',
    long: 'The 9th house is the long view: philosophy, higher study, foreign travel and the people who teach you. It is where you go looking for meaning rather than information. Jyotish treats it as the strongest house of fortune and calls it the house of dharma.',
  },
  {
    n: 10,
    short: 'Career, public standing and what you are known for.',
    long: 'The 10th house is the top of the chart: career, reputation and what strangers could tell you about yourself. It answers what you are seen doing. Jyotish reads it for karma in the sense of action, and for profession.',
  },
  {
    n: 11,
    short: 'Friends, networks, and what you are hoping for.',
    long: 'The 11th house covers friends, communities, networks and your hopes for what comes next. It is where individual effort turns into something collective. Jyotish reads it directly as gains and income received.',
  },
  {
    n: 12,
    short: 'Rest, retreat, the unconscious and what happens offstage.',
    long: 'The 12th house is everything that happens away from view: sleep, retreat, hospitals, foreign places, the unconscious, and what you spend with no return. It is the house of loss and of release, which are the same movement seen from different sides. Jyotish also reads it for expenditure and for liberation.',
  },
];

const ORDINALS = [
  '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th',
];
const ORDINAL_WORDS = [
  'first', 'second', 'third', 'fourth', 'fifth', 'sixth',
  'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth',
];

const HOUSES: GlossaryTerm[] = HOUSE_ROWS.map((h) => ({
  id: `house-${h.n}`,
  title: `${ORDINALS[h.n - 1]} house`,
  short: h.short,
  long: h.long,
  category: 'western',
  aliases: [
    `house ${h.n}`,
    `${ORDINAL_WORDS[h.n - 1]} house`,
    `${h.n}th house`,
  ],
}));

// --- Western signs ----------------------------------------------------------

interface SignRow {
  id: string;
  title: string;
  rashi: string;
  modality: string;
  element: string;
  ruler: string;
  short: string;
  body: string;
}

const SIGN_ROWS: SignRow[] = [
  {
    id: 'aries', title: 'Aries', rashi: 'Mesha', modality: 'cardinal', element: 'fire', ruler: 'Mars',
    short: 'Cardinal fire — moves first and asks the question afterwards.',
    body: 'Aries opens the tropical zodiac at the spring equinox, and its keyword is initiative.',
  },
  {
    id: 'taurus', title: 'Taurus', rashi: 'Vrishabha', modality: 'fixed', element: 'earth', ruler: 'Venus',
    short: 'Fixed earth — steady, sensory, and impossible to hurry.',
    body: "Taurus is the zodiac's holding pattern: comfort, worth, and things built to last.",
  },
  {
    id: 'gemini', title: 'Gemini', rashi: 'Mithuna', modality: 'mutable', element: 'air', ruler: 'Mercury',
    short: 'Mutable air — curious, quick, and talking to everybody.',
    body: 'Gemini collects information and passes it straight on, preferring range over depth.',
  },
  {
    id: 'cancer', title: 'Cancer', rashi: 'Karka', modality: 'cardinal', element: 'water', ruler: 'the Moon',
    short: 'Cardinal water — protective, tidal, and led by feeling.',
    body: 'Cancer builds shelter, for itself and for other people; its first instinct is to look after something.',
  },
  {
    id: 'leo', title: 'Leo', rashi: 'Simha', modality: 'fixed', element: 'fire', ruler: 'the Sun',
    short: 'Fixed fire — warm, generous, and content to be looked at.',
    body: 'Leo is the sign of self-expression and heart, ruled by the Sun itself.',
  },
  {
    id: 'virgo', title: 'Virgo', rashi: 'Kanya', modality: 'mutable', element: 'earth', ruler: 'Mercury',
    short: 'Mutable earth — improves things and notices what others miss.',
    body: 'Virgo takes the raw material and refines it; the underlying drive is usefulness.',
  },
  {
    id: 'libra', title: 'Libra', rashi: 'Tula', modality: 'cardinal', element: 'air', ruler: 'Venus',
    short: 'Cardinal air — weighs, balances, and cannot stand a bad room.',
    body: 'Libra is the sign of relationship and proportion, opening the second half of the zodiac.',
  },
  {
    id: 'scorpio', title: 'Scorpio', rashi: 'Vrischika', modality: 'fixed', element: 'water',
    ruler: 'Mars, or Pluto in modern Western practice',
    short: 'Fixed water — private, intense, and all-in once committed.',
    body: 'Scorpio deals in depth: what is hidden, what is shared, and what gets transformed.',
  },
  {
    id: 'sagittarius', title: 'Sagittarius', rashi: 'Dhanu', modality: 'mutable', element: 'fire', ruler: 'Jupiter',
    short: 'Mutable fire — restless, blunt, and always aiming further out.',
    body: 'Sagittarius wants the bigger picture, whether it reaches it by travel, belief or argument.',
  },
  {
    id: 'capricorn', title: 'Capricorn', rashi: 'Makara', modality: 'cardinal', element: 'earth', ruler: 'Saturn',
    short: 'Cardinal earth — patient, structural, playing a long game.',
    body: 'Capricorn builds things designed to outlast the effort of building them.',
  },
  {
    id: 'aquarius', title: 'Aquarius', rashi: 'Kumbha', modality: 'fixed', element: 'air',
    ruler: 'Saturn, or Uranus in modern Western practice',
    short: 'Fixed air — principled, detached, and loyal to the idea.',
    body: 'Aquarius is the sign of systems and of the group weighed above the individual.',
  },
  {
    id: 'pisces', title: 'Pisces', rashi: 'Meena', modality: 'mutable', element: 'water',
    ruler: 'Jupiter, or Neptune in modern Western practice',
    short: 'Mutable water — porous, imaginative, and hard to pin down.',
    body: 'Pisces closes the zodiac by dissolving its edges; empathy is its default setting.',
  },
];

const SIGNS: GlossaryTerm[] = SIGN_ROWS.map((s) => ({
  id: s.id,
  title: s.title,
  short: s.short,
  long: `${s.body} It is ${s.modality} ${s.element}, ruled by ${s.ruler}. Jyotish calls the same sign ${s.rashi} and measures it against the stars, so its dates fall about three weeks later than the tropical ones you already know.`,
  category: 'western',
  aliases: [s.rashi, `${s.title} sign`],
}));

// --- Product ----------------------------------------------------------------

const PRODUCT: GlossaryTerm[] = [
  {
    id: 'ai-credits',
    title: 'AI credits',
    short: 'What premium Astrally AI generations spend. Purchased credits never expire.',
    long:
      'Credits power the heavier generations: Go Deeper answers, long reports and Master Readings. Ordinary chat with Astrally AI does not spend them. If a generation fails, your credits are restored automatically — you are never charged for something you did not receive.',
    category: 'product',
    aliases: ['credits', 'credit', 'ai credit'],
  },
  {
    id: 'base',
    title: 'Base',
    short: 'The one-time purchase that unlocks the full astrology utility and AI.',
    long:
      'Base is a single small payment that permanently unlocks your complete charts and Astrally AI, on Android and web, tied to your account. It also includes five lifetime outgoing connection requests; incoming requests and existing chats are always unlimited. Connections stay optional and switched off until you choose otherwise.',
    category: 'product',
    aliases: ['base tier', 'unlock', 'one-time purchase'],
  },
  {
    id: 'pro',
    title: 'Astrally Pro',
    short: 'The subscription: fair-use unlimited AI chat and the full social graph.',
    long:
      'Pro is the optional subscription on top of Base. It adds fair-use unlimited AI chat, the complete compatibility breakdown on other people\'s profiles, and a daily allowance of outgoing connection requests. Fair-use limits exist to keep Astrally fast for everyone, and normal daily use is never restricted.',
    category: 'product',
    aliases: ['astrally pro', 'subscription', 'pro tier'],
  },
  {
    id: 'compatibility-score',
    title: 'Compatibility score',
    short: 'A blended fit signal — never proof that someone is right or safe for you.',
    long:
      'Astrally\'s compatibility score combines several signals, including the Jyotish ashtakoota calculation, communication style and the intentions you each stated. It decides who you might see first; it does not rank people. No score is evidence that a person is safe, kind or right for you — that part is always yours to work out by talking to them.',
    category: 'product',
    aliases: ['compatibility', 'fit score', 'match score'],
  },
  {
    id: 'connection-request',
    title: 'Connection request',
    short: 'A private invitation to talk — they can accept or decline, quietly.',
    long:
      'A connection request is how you reach someone on Astrally: a private ask, with an optional note, that only that person sees. Nothing opens until they accept, a decline is never explained back to you, and there is no public feed and no swiping. Base includes five lifetime outgoing requests; Pro adds a daily allowance.',
    category: 'product',
    aliases: ['request', 'intro request', 'introduction request', 'connect'],
  },
];

// --- Registry ---------------------------------------------------------------

export const TERMS: GlossaryTerm[] = [
  ...SHARED,
  ...JYOTISH_CORE,
  ...NAKSHATRAS,
  // Before SIGNS deliberately: alias registration is first-writer-wins, so a
  // bare Indian name ('Mesha') resolves to the rashi entry, Jyotish-first,
  // while 'aries' stays with the Western entry because ids beat aliases.
  ...RASHIS,
  ...GRAHAS,
  ...BHAVAS,
  ...WESTERN_CORE,
  ...SIGNS,
  ...HOUSES,
  ...PRODUCT,
];

export const TERM_BY_ID: Record<string, GlossaryTerm> = Object.fromEntries(
  TERMS.map((term) => [term.id, term]),
);

function normalise(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** id + alias lookup. First writer wins so an id can never be shadowed. */
const LOOKUP = new Map<string, GlossaryTerm>();
for (const term of TERMS) LOOKUP.set(normalise(term.id), term);
for (const term of TERMS) {
  for (const alias of term.aliases ?? []) {
    const key = normalise(alias);
    if (!LOOKUP.has(key)) LOOKUP.set(key, term);
  }
}

/**
 * Resolves an id, an alias, or a dotted chart-fact id from the astro engine
 * (`jyotish.lagna`, `jyotish.yoga.gajakesari`, `gochar.saturn`, `western.mc`)
 * by walking the path from its most specific segment outwards.
 *
 * The dotted families `rashi.X`, `graha.X` and `bhava.N` are real entry ids
 * and resolve exactly. An unknown dotted id degrades to its broadest known
 * concept — `nakshatra.rohini` hits the Rohini entry via its tail segment,
 * while a hypothetical `nakshatra.zzz` still lands on the `nakshatra` entry —
 * so a chart fact can always open *something* true about itself.
 */
export function findTerm(idOrAlias: string | undefined | null): GlossaryTerm | undefined {
  if (!idOrAlias) return undefined;
  const key = normalise(idOrAlias);
  if (!key) return undefined;
  const direct = LOOKUP.get(key);
  if (direct) return direct;

  const parts = key.split('.');
  if (parts.length > 1) {
    for (let i = parts.length - 1; i >= 0; i--) {
      const tail = LOOKUP.get(parts.slice(i).join('.'));
      if (tail) return tail;
      const segment = LOOKUP.get(parts[i]);
      if (segment) return segment;
    }
  }
  return undefined;
}

/** Case-insensitive search over title, id and aliases. */
export function searchTerms(query: string): GlossaryTerm[] {
  const q = normalise(query);
  if (!q) return TERMS;
  return TERMS.filter((term) => {
    if (normalise(term.title).includes(q)) return true;
    if (term.id.includes(q.replace(/ /g, '-'))) return true;
    return (term.aliases ?? []).some((alias) => normalise(alias).includes(q));
  });
}
